import { FC, Fragment, useEffect, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Models
import IPage from 'models/Page';
import IQuiz from 'models/Quiz';
// Types
import PageTypes from 'types/PageTypes';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getQuiz } from 'store/quiz/quizAsync';
// Actions
import { setQuiz } from 'store/quiz/quizSlice';
import { setPageIndexByRef, setPages } from 'store/pages/pagesSlice';
import { setPreviewPageMode } from 'store/player/playerSlice';
// Selectors
import { selectResultId, selectUserId } from 'store/auth/authSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
// Components
import Snackbar from 'components/Snackbar';
import AppLoader from 'components/AppLoader';
// Services
import StorageService from 'services/Storage.service';
// i18next
import { getSearchParam } from 'utilities/Utilities';

const QuizRootPage:FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug:string }>();
  const { search } = window.location;

  const hasHash = getSearchParam(search, 'hasHash');

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const resultId:string | null = useAppSelector(selectResultId);
  const userId:string | null = useAppSelector(selectUserId);

  const navigateTo = useNavigateTo();

  const [ loaded, setLoaded ] = useState<boolean | undefined>(undefined);
  const [ quizInitialized, setQuizInitialized ] = useState(false);
  const [ pageRef, setPageRef ] = useState<string | null>(null);

  useEffect(() => {
    if ( !hasHash ) setLoaded(true);
    window.addEventListener('message', onReceiveMessage, false);

    window.parent.postMessage({ type: 'READY' }, '*');

    return () => {
      window.removeEventListener('message', onReceiveMessage, false);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( loaded ){
      const storageQuiz = StorageService.getQuiz();
      if ( storageQuiz ){
        dispatch(setQuiz(storageQuiz));
      } else {
        dispatch(getQuiz((slug as string)));
      }
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (typeof quiz === 'undefined') {
      navigate('/404-not-found');
    }
    if ( quiz && quiz._id ){
      // Set default primary
      document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
      document.documentElement.style.setProperty('--primaryFont', quiz.theme.primaryFont);
      document.documentElement.style.setProperty('--primaryFontColor', quiz.theme.primaryFontColor);
      document.documentElement.style.setProperty('--primaryButtonGradientStart', quiz.theme.primaryButtonGradientStart);
      document.documentElement.style.setProperty('--primaryButtonGradientEnd', quiz.theme.primaryButtonGradientEnd);
      document.documentElement.style.setProperty('--primaryButtonFontColor', quiz.theme.primaryButtonFontColor);
      document.documentElement.style.setProperty('--selectedAnswerButtonGradientStart', quiz.theme.selectedAnswerButtonGradientStart);
      document.documentElement.style.setProperty('--selectedAnswerButtonGradientEnd', quiz.theme.selectedAnswerButtonGradientEnd);
      document.documentElement.style.setProperty('--selectedAnswerButtonFontColor', quiz.theme.selectedAnswerButtonFontColor);

      if ( quiz.wallpaperImage ){
        document.body.style.backgroundImage = `url(${quiz.wallpaperImage})`;
      }

      if ( resultId ){
        if ( userId ){
          navigateTo('post-quiz');
        } else {
          navigateTo('register');
        }
      }

      if (quiz.pages) {
        let pages:IPage[] = quiz.pages;
        let questionPages = quiz.pages.filter((page:IPage) => page.type === PageTypes.Question);
        if (quiz.questionsSettings.randomizeOrder) {
          questionPages = questionPages.sort(() => Math.random() - 0.5);
          pages = pages.map(page => {
            if (page.type === PageTypes.Question) {
              const question = questionPages.splice(0, 1)[0];
              return question;
            }
            return page;
          });
        }

        const limit = quiz.questionsSettings.limit;
      
        if ( limit && limit > 0 && !pageRef ){
          let questionsLength = 0;
          const nextPages:IPage[] = [];
          pages.forEach((page: IPage) => {
            if (page.type !== PageTypes.Question) {
              nextPages.push(page)
            } else {
              if (questionsLength < limit) {
                nextPages.push(page);
                questionsLength++;
              }
            }
          })
          pages = nextPages;
        }

        dispatch(setPages(pages));
        if (pageRef) {
          dispatch(setPageIndexByRef(pageRef));
        }
      }

      setQuizInitialized(true);
    }
    // eslint-disable-next-line
  }, [quiz]);

  const onReceiveMessage = (event:any) => {
    const { action, quizHash, page, pageRef } = event.data;
    if ( action === 'preview' && quizHash ){
      StorageService.setQuiz(quizHash);
      setLoaded(true);
      if (pageRef) {
        setPageRef(pageRef);
      }
      if (page) {
        dispatch(setPreviewPageMode(true));
        navigateTo(page);
      }
    }
  }

  if ( quiz === null || !quizInitialized ) return <AppLoader />;
  return (
    <Fragment>
      <Outlet />
      <Snackbar />
    </Fragment>
  );
}

export default QuizRootPage;
